/**
 * Client-side JWT decoding (NO signature verification).
 *
 * The admin SPA only DECODES the access token to read identity claims for
 * route gating + UX. The oxbo-auth REST API is the real authority: every
 * protected call is independently verified server-side against the shared
 * JWKS, so a tampered client token gains nothing. We never trust these claims
 * for anything beyond what to render.
 *
 * The authoritative role claim is `system_roles` (a list[str]) — NOT the lossy
 * scalar `system_role` (= system_roles[0]) that /v1/users/me currently returns.
 */

export interface OxboJwtClaims {
  sub?: string;
  user_id?: string;
  org_id?: string;
  email?: string;
  org_roles?: string[];
  /** Authoritative global system-role list, e.g. ["super_admin","industry_trustee"]. */
  system_roles?: string[];
  /** Lossy scalar (system_roles[0]); kept only for backward-compat reads. */
  system_role?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/** Decode a base64url segment to a UTF-8 string (browser-safe). */
function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  // Decode UTF-8 bytes (handles non-ASCII in claims).
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Decode JWT claims. Returns null for malformed/undecodable tokens. */
export function decodeJwt(token: string | null | undefined): OxboJwtClaims | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1])) as OxboJwtClaims;
  } catch {
    return null;
  }
}

/** True if the token is expired (or expiring within `skewSeconds`). */
export function isExpired(claims: OxboJwtClaims | null, skewSeconds = 30): boolean {
  if (!claims?.exp) return true;
  return Date.now() / 1000 >= claims.exp - skewSeconds;
}

/**
 * Extract the full system-role list from claims, tolerant of shape:
 *   - prefer the `system_roles` array (authoritative);
 *   - fall back to the lossy scalar `system_role` wrapped in an array.
 */
export function systemRolesFromClaims(claims: OxboJwtClaims | null): string[] {
  if (!claims) return [];
  if (Array.isArray(claims.system_roles)) return claims.system_roles;
  if (typeof claims.system_role === "string" && claims.system_role) {
    return [claims.system_role];
  }
  return [];
}

export const SUPER_ADMIN = "super_admin";
export const INDUSTRY_TRUSTEE = "industry_trustee";

export function hasSuperAdmin(roles: string[]): boolean {
  return roles.includes(SUPER_ADMIN);
}
